import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";
import moment from "moment";
import { ExpandMore } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Radio,
  TableCell,
  Tooltip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import TrueIcon from "@iconify/icons-mdi/check";
import CheckBold from "@iconify/icons-mdi/check-bold";
import FalseIcon from "@iconify/icons-mdi/close";
import CloseThick from "@iconify/icons-mdi/close-thick";
import UnlockIcon from "@iconify/icons-mdi/lock-open-variant-outline";
import LockIcon from "@iconify/icons-mdi/lock-outline";
import MessageOutlineIcon from "@iconify/icons-mdi/message-outline";
import MessageTextIcon from "@iconify/icons-mdi/message-text";
import pencilIcon from "@iconify/icons-mdi/pencil";
import { InlineIcon } from "@iconify/react";
import { MyDocumentsActionCell } from "my-documents/components";
import { myDocumentsIsDocument } from "my-documents/states/is-document-state";
import { myDocumentsShowDocument } from "my-documents/states/show-document-state";
import { MyLeaveAvailableRoles } from "my-leave/components";
import MyLeaveBarDashboard from "my-leave/components/bar-dashboard/bar-dashboard";
import ApplicationState from "my-tax/components/application-state";
import {
  MyTimeDateHeaderTooltip,
  MyTimeMessagesIndicator,
} from "my-time/components";
import {
  OcAvatar,
  OcCurrencyComponent,
  OcDataTableBodyCellAddIcon,
  OcDataTableBodyCellArrowIcon,
  OcDataTableBodyCellArrowIconIsNotExaminer,
  OcDataTableBodyCellEditIcon,
  OcDataTableBodyCellState,
  OcConfirmationDialog
} from "oc/components";
import { getFormatTextWidthValue, getFullname } from "oc/utils";
import parsePipedName from "oc/utils/get-parse-pipedName";
import EyeIcon from "@iconify/icons-mdi/eye";

export const OcDataTableBodyCell = ({
  align,
  row,
  id,
  type,
  onSelectRow,
  selectedRowValue,
  inlineEdit,
  canCopy,
  rowSpanData,
  rowSpanField,
  colorize = false,
  isNotExaminer,
  selectedRowId,
  isMobile,
  filterSelected,
  filterField,
}: {
  align: "left" | "center" | "right" | "inherit" | "justify";
  row: any;
  id: string;
  type: string;
  onSelectRow: (data: any) => void;
  selectedRowValue: any;
  inlineEdit: boolean;
  canCopy: boolean;
  rowSpanData: any[];
  rowSpanField: string;
  colorize?: boolean;
  isNotExaminer: boolean;
  selectedRowId: string;
  isMobile: boolean;
  filterSelected?: string[];
  filterField?: string;
}) => {
  let rowSpan = 1;
  const theme = useTheme();

  if (rowSpanData?.length > 1) {
    if (rowSpanData[0]?.[rowSpanField] === row?.[rowSpanField]) {
      rowSpan = rowSpanData?.length;
    } else {
      rowSpan = 0;
    }
  }
  let color = "";
  let dayColor = "";
  if (colorize) {
    if (isMobile) {
      if (row?.state === "requested") {
        dayColor = "#F3AA18";
      }
      if (row?.state === "approved") {
        dayColor = "#2FA84F";
      }
    } else {
      if (row?.state === "requested") {
        color = "#F3AA18";
      }
      if (row?.state === "approved") {
        color = "#2FA84F";
      }
    }
    if (row.dayType === "REST_DAY") {
      dayColor = "#1EA5FC";
    }
    if (row.dayType === "PAID_HOLLIDAY") {
      dayColor = "#cc0033";
    }
  }

  const { t, i18n } = useTranslation();

  const handleRadioSelected = (row: any) => {
    onSelectRow({ key: "code", value: row.code });
  };

  const handleLeaveAction = (action: string) => {
    onSelectRow({ id: row._id, action: action });
  };

  const handleCafeteriaAction = (action: string) => {
    onSelectRow({ item: row, action: action });
  };

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const showDocument = useRecoilValue(myDocumentsShowDocument);
  const isDocument = useRecoilValue(myDocumentsIsDocument);

  let isExpanded =
    selectedRowId === row?._id || (selectedRowId === row?.contract) === true;

  if (showDocument || isDocument) {
    isExpanded = false;
  }
  const maxLength = isMobile ? 10 : 30;

  const HUF = new Intl.NumberFormat("hu-HU", {
    style: "currency",
    currency: "HUF",
    maximumSignificantDigits: 3,
  });

  if (type === "billable") {
    const trueIcon = (
      <InlineIcon icon={TrueIcon} width="24px" height="24px" color="#2FA84F" />
    );
    const falseIcon = (
      <InlineIcon icon={FalseIcon} width="24px" height="24px" color="#cc0033" />
    );
    return (
      <TableCell align={align}>
        <Box>{row.billable ? trueIcon : falseIcon}</Box>
      </TableCell>
    );
  }

  if (type === "currency" && id === "value") {
    return (
      <TableCell align={align}>
        <OcCurrencyComponent
          value={Number(row[id])}
        >
        </OcCurrencyComponent>
      </TableCell>
    );
  }

  if (id === "duration")
    return (
      <TableCell align={align}>
        <Box
          style={{
            fontWeight: "bold",
          }}
        >
          {row?.duration}
        </Box>
      </TableCell>
    );

  if (id === "name" && row?.__typename === "Timesheet")
    return (
      <TableCell align={align}>
        <Box>{row?.name}</Box>
      </TableCell>
    );

  if (id === "summaryCost")
    return (
      <TableCell align={align}>
        <Box>{HUF.format(row.summaryCost)}</Box>
      </TableCell>
    );

  if (id === "name" && row?.color)
    return (
      <TableCell align={align}>
        <Box
          style={{
            color: row?.validTo ? theme.palette.gray60.main : "inherit",
          }}
        >
          {row?.name}
        </Box>
      </TableCell>
    );

  if (id === "color")
    return (
      <TableCell align={align}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
        >
          <Box
            style={{
              width: "16px",
              height: "16px",
              borderRadius: "50%",
              backgroundColor: row.color,
            }}
          />
        </Box>
      </TableCell>
    );

  if (id === "actionButton")
    return (
      <TableCell align={align} style={{ padding: "0px 8px" }}>
        <MyDocumentsActionCell document={row} isMobile={isMobile} />
      </TableCell>
    );

  if (id === "actionArrow")
    return (
      <TableCell align={align}>
        <Box>
          <IconButton
            id="showDocuments"
            aria-expanded={isExpanded}
            aria-label="show more"
            style={{
              padding: 0,
              transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            <ExpandMore />
          </IconButton>
        </Box>
      </TableCell>
    );

  if (id === "designation")
    return (
      <TableCell align={align}>
        <Box>{row?.description}</Box>
      </TableCell>
    );

  if (row?.__typename === "Cost" && id === "value")
    return (
      <TableCell align={align}>
        <Box
          style={{
            color: row?.validTo ? theme.palette.gray60.main : "inherit",
          }}
        >
          {row?.value}
        </Box>
      </TableCell>
    );

  if (id === "description")
    return (
      <TableCell align={align}>
        {row?.description?.length > maxLength && !isMobile ? (
          <Tooltip title={row?.description} placement="top-start">
            <Box>{getFormatTextWidthValue(row?.description, maxLength)}</Box>
          </Tooltip>
        ) : (
          <Box>{row?.description}</Box>
        )}
      </TableCell>
    );

  if (id === "uploaded")
    return (
      <TableCell align={align}>
        <Box>{new Date(row?.createdAt).toLocaleString()}</Box>
      </TableCell>
    );

  if (id === "filename")
    return (
      <TableCell align={align}>
        {row?.filename?.length > maxLength && !isMobile ? (
          <Tooltip title={row?.filename} placement="top-start">
            <Box>{getFormatTextWidthValue(row?.filename, maxLength)}</Box>
          </Tooltip>
        ) : (
          <Box>{row?.filename}</Box>
        )}
      </TableCell>
    );

  if (id === "examinerFullname")
    return (
      <TableCell align={align} style={{ padding: "0px 8px" }}>
        <Box>{getFullname(row)}</Box>
      </TableCell>
    );

  if (
    (id === "requestCount" && type === "myTimeBadges") ||
    (id === "recordCount" && type === "myTimeBadges") ||
    type === "myTimesheetBadges"
  ) {
    return (
      <TableCell align={align} style={{ padding: "0px 8px" }}>
        {row[id] > 0 && (
          <Box
            display="inline-block"
            width="22px"
            height="22px"
            style={{
              backgroundColor: "#cc0033",
              color: "white",
              borderRadius: "2px",
              textAlign: "center",
              fontSize: "12px",
              letterSpacing: "0.4px",
              paddingTop: "1px",
              marginRight: "8px",
            }}
          >
            {row[id]}
          </Box>
        )}
      </TableCell>
    );
  }

  if (id === "role")
    return (
      <TableCell align={align} style={{ padding: "0px 8px" }}>
        <Box>{t(`MY_HR_ROLE_${row.role.toUpperCase()}`)}</Box>
      </TableCell>
    );

  if (type === "pipedName" || (id === "name" && align !== "left")) {
    return <TableCell align={align}>{parsePipedName(row[id], i18n)}</TableCell>;
  }

  if (type === "acknowledgement") {
    const isAcknowledged = row?.isAcknowledged ? true : false;

    return (
      <TableCell align={align} style={{ padding: "0px 8px" }}>
        <Box
          id={`acknowledgement-${isAcknowledged ? 'true' : 'false'}-${row._id}`}
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
        >
          <InlineIcon
            width="24px"
            height="24px"
            icon={isAcknowledged ? TrueIcon : FalseIcon}
            color="#919699"
          />
        </Box>
      </TableCell>
    );
  }

  if (type === "authorization") {
    const isAuthorized = row?.authorizedAt ? true : false;

    return (
      <TableCell align={align} style={{ padding: "0px 8px" }}>
        <Box
          id={`authorization-${isAuthorized ? 'true' : 'false'}-${row._id}`}
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
        >
          <InlineIcon
            width="24px"
            height="24px"
            icon={isAuthorized ? TrueIcon : FalseIcon}
            color="#919699"
          />
        </Box>
      </TableCell>
    );
  }

  if (type === "viewWorkSchedule") {
    return (
      <TableCell
        align={align}
        style={{ padding: "0px 8px" }}
        sx={{ cursor: "pointer" }}
      >
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          onClick={() => onSelectRow({ action: "view", row })}
        >
          <Box
            id={`view-work-schedule-icon-${row._id}`}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            width="36px"
            height="36px"
            sx={{
              borderRadius: "50%",
              border: `1px solid #b2b4b6`,
            }}
          >
            <InlineIcon
              width="24px"
              height="24px"
              icon={EyeIcon}
              color={theme.palette.gray80.main}
            />
          </Box>
        </Box>
      </TableCell>
    );
  }

  if (type === "approvement") {
    const isApproved = row?.isApproved ? true : false;

    const dialogTitle = isApproved
      ? t("MY_TIME_UNLOCK_CLOSE_MONTH_TITLE")
      : t("MY_TIME_CONFIRM_CLOSE_MONTH_TITLE");
    const dialogTexts = isApproved
      ? [t("MY_TIME_UNLOCK_CLOSE_MONTH_INFO")]
      : [
        t("MY_TIME_CONFIRM_GROUP_CLOSE_MONTH_TEXT"),
        t("MY_TIME_CONFIRM_GROUP_CLOSE_MONTH_TEXT_RESPONSIBILITY"),
      ];
    const buttonText = isApproved ? t("MY_HR_UNLOCK") : t("MY_TIME_CLOSE_MONTH");

    const handleConfirm = () => {
      setShowConfirmDialog(false);
      if (isApproved) {
        onSelectRow({ action: "open", row });
      } else {
        onSelectRow({ action: "close", row });
      }
    };

    const handleDialogCancel = () => {
      setShowConfirmDialog(false);
    };

    return (
      <TableCell
        align={align}
        style={{ padding: "0px 8px" }}
        sx={{ cursor: "pointer" }}
      >
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          onClick={() => setShowConfirmDialog(true)}
        >
          <Box
            id={`approvement-lock-icon-${row._id}`}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            width="36px"
            height="36px"
            sx={{
              borderRadius: "50%",
              border: `1px solid ${isApproved ? "#2FA84F" : "#cc0033"}`,
            }}
          >
            <InlineIcon
              width="24px"
              height="24px"
              icon={isApproved ? LockIcon : UnlockIcon}
              color={isApproved ? "#2FA84F" : "#cc0033"}
            />
          </Box>
        </Box>
        <OcConfirmationDialog
          showDialog={showConfirmDialog}
          onClose={handleConfirm}
          onCancel={handleDialogCancel}
          title={dialogTitle}
          texts={dialogTexts}
          okButtonText={buttonText}
          cancelButtonText={t("OC_CANCEL")}
        />
      </TableCell>
    );
  }

  if (type === "availableRoles")
    return <MyLeaveAvailableRoles align={align} row={row} />;

  if (type === "baseLeaveBar")
    return (
      <TableCell align={align || "center"} style={{ padding: "0px 8px" }}>
        <Box>
          <MyLeaveBarDashboard id="base-leave-days-bar" row={row} />
        </Box>
      </TableCell>
    );
  if (type === "avatar")
    return (
      <TableCell align={align || "center"} style={{ padding: "0px 8px" }}>
        <Box display="flex" flexDirection="row" justifyContent="center">
          <OcAvatar
            employee={
              row.employee
                ? row.employee
                : row.employeeCode
                  ? row.employeeCode
                  : row.code
            }
            style={{
              width: "36px",
              height: "36px",
              marginLeft: "-8px",
            }}
          />
        </Box>
      </TableCell>
    );
  if (type === "checkbox")
    return (
      <TableCell align={align || "center"} style={{ padding: "0px 8px" }}>
        <Box>
          <Checkbox
            checked={row.active}
            inputProps={{
              "aria-labelledby": row.fullname,
            }}
            style={{ padding: "5px" }}
          />
        </Box>
      </TableCell>
    );
  if (type === "radio")
    return (
      <TableCell align={align || "center"} style={{ padding: "0px 8px" }}>
        <Radio
          id={row?.code}
          checked={row.code === selectedRowValue}
          onChange={() => handleRadioSelected(row)}
          value="a"
          name="radio-button-demo"
          size="small"
          inputProps={{ "aria-label": "A" }}
          style={{ padding: "5px", color: "#5E6366" }}
        />
      </TableCell>
    );

  if (type === "date") {
    let dateFormat = new Date(row[id]).toLocaleDateString();
    return <TableCell align={align}>{dateFormat}</TableCell>;
  }

  if (type === "dateTime") {
    let dateFormat =
      new Date(row[id]).toLocaleDateString() +
      " " +
      new Date(row[id]).toLocaleTimeString();
    return <TableCell align={align}>{dateFormat}</TableCell>;
  }

  if (type === "dateWithdayName") {
    if (rowSpan === 0) return null;
    let date = new Date(row[id]);
    let dayName = moment(date).locale(i18n.language).format("dd");
    dayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);
    let dateFormat =
      i18n.language === "en"
        ? moment(date).format("MM.DD.YYYY. ") + dayName
        : moment(date).format("YYYY.MM.DD. ") + dayName;

    return (
      <TableCell align={align} rowSpan={rowSpan} style={{ color: dayColor }}>
        {dateFormat}
      </TableCell>
    );
  }
  if (type === "myTimeType") {
    return (
      <TableCell align={align}>
        {row.myTimeType ? t(`MY_TIME_${row.myTimeType}`) : null}
      </TableCell>
    );
  }

  if (type === "dayType") {
    if (rowSpan === 0) return null;
    return (
      <TableCell align={align} style={{ color: dayColor }} rowSpan={rowSpan}>
        {t(`MY_TIME_${row.dayType}`)}
      </TableCell>
    );
  }

  if (type === "myTimeDate") {
    if (rowSpan === 0) return null;
    let date = new Date(row[id]);
    let dayName = moment(date).locale(i18n.language).format("dd");
    dayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);
    let dateFormat =
      moment(date).locale(i18n.language).format("YYYY.MM.DD. ") + dayName;

    const { workTime, overtime, voluntaryOvertime } =
      row?.countTimeData?.(date) ?? {};

    return (
      <MyTimeDateHeaderTooltip
        workTime={workTime}
        overtime={overtime}
        voluntaryOvertime={voluntaryOvertime}
        isOffRange={false}
        date={date}
      >
        <TableCell align={align} rowSpan={rowSpan} style={{ color: dayColor }}>
          {dateFormat}
        </TableCell>
      </MyTimeDateHeaderTooltip>
    );
  }

  if (type === "myTimeType") {
    return <TableCell align={align}>{t(row.myTimeType)}</TableCell>;
  }

  if (type === "dayType") {
    if (rowSpan === 0) return null;
    return (
      <TableCell align={align} style={{ color: dayColor }} rowSpan={rowSpan}>
        {t(row.dayType)}
      </TableCell>
    );
  }

  if (type === "i18nText") {
    return <TableCell align={align}>{t(`MY_TIME_${row[id]}`)}</TableCell>;
  }

  if (type === "taxDeclarationType") {
    if (row?.isModifiedDeclaration === 1) {
      return (
        <TableCell align={align}>
          {`${t(`MY_TAX_${row[id].toUpperCase()}`)} (${t("MY_TAX_MODIFIED_DECLARATION")})`}
        </TableCell>
      );
    }
    return (
      <TableCell align={align}>
        {t(`MY_TAX_${row[id].toUpperCase()}`)}
      </TableCell>
    );
  }

  if (id === "effectiveWorkDays" || id === "fulfilledLeave") {
    return <TableCell align={align}>{row[id].toLocaleString()}</TableCell>;
  }

  if (type === "period") {
    let startTime =
      new Date(row.startTime).toLocaleDateString() +
      " " +
      moment(row.startTime).format("HH:mm");

    let endTime =
      new Date(row.endTime).toLocaleDateString() +
      " " +
      moment(row.endTime).format("HH:mm");

    const period = row.startTime ? `${startTime} - ${endTime}` : "";

    return (
      <TableCell align={align} style={{ color: color }}>
        {period}
      </TableCell>
    );
  }

  if (type === "messageIndicator") {
    return (
      <TableCell style={{ textAlign: align }}>
        <InlineIcon
          width="20px"
          height="20px"
          icon={
            row?.messages?.length > 0 || row?.messageCount > 0
              ? MessageTextIcon
              : MessageOutlineIcon
          }
          style={{
            color: theme.palette.gray60.main,
          }}
        />
      </TableCell>
    );
  }

  if (type === "myTimeMessageIndicator") {
    return (
      <TableCell style={{ textAlign: align }}>
        <MyTimeMessagesIndicator row={row} />
      </TableCell>
    );
  }

  if (type === "state") {
    return (
      <TableCell align={align}>
        <OcDataTableBodyCellState label={t(row[id])} />
      </TableCell>
    );
  }

  if (type === "leaveActions") {
    return (
      <TableCell align="center">
        <Box display="flex" flexDirection="row" justifyContent="center">
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            width="36px"
            height="36px"
            style={{
              backgroundColor: "#2FA84F",
              borderRadius: "4px",
              color: "white",
              cursor: "pointer",
            }}
            onClick={() => handleLeaveAction("accepted")}
          >
            <InlineIcon width="20px" height="20px" icon={CheckBold} />
          </Box>
          <Box width="4px" />
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            width="36px"
            height="36px"
            style={{
              backgroundColor: "#cc0033",
              borderRadius: "4px",
              color: "white",
              cursor: "pointer",
            }}
            onClick={() => handleLeaveAction("rejected")}
          >
            <InlineIcon width="20px" height="20px" icon={CloseThick} />
          </Box>
        </Box>
      </TableCell>
    );
  }

  if (type === "nameAndJobClass") {
    return (
      <TableCell style={{ padding: "8px 8px" }}>
        <Box style={{ fontSize: "16px", letterSpacing: "0.44px" }}>
          {row.name}
        </Box>
        <Box
          style={{
            fontSize: "14px",
            letterSpacing: "0.25px",
            color: "#919699",
            paddingLeft: "16px",
          }}
        >
          {row.jobClass}
        </Box>
      </TableCell>
    );
  }

  if (type === "periodAndWorkDays") {
    return (
      <TableCell style={{ padding: "8px 8px" }}>
        <Box style={{ fontSize: "16px", letterSpacing: "0.44px" }}>
          {new Date(row.startDate).toLocaleDateString()} -{" "}
          {new Date(row.endDate).toLocaleDateString()}
        </Box>
        <Box
          style={{
            fontSize: "14px",
            letterSpacing: "0.25px",
            color: "#919699",
            paddingLeft: "16px",
          }}
        >
          {row.workDays} {t("OC_WORKDAYS")}
        </Box>
      </TableCell>
    );
  }

  if (type === "leaveBadges") {
    let past = row?.pastRequestedLeavesCount || 0;
    let current = row?.currentRequestedLeavesCount || 0;
    let future = row?.futureRequestedLeavesCount || 0;

    // Alapértelmezett: minden látszik
    let currentOrPast = past + current;
    let showFuture = future;

    // Ha van aktív filter, akkor csak a kiválasztott kategóriák látszanak
    if (filterField === "leaveBadges" && filterSelected && filterSelected.length > 0) {
      currentOrPast = 0;
      if (filterSelected.includes("past")) currentOrPast += past;
      if (filterSelected.includes("current")) currentOrPast += current;
      showFuture = filterSelected.includes("future") ? future : 0;
    }

    return (
      <TableCell align="center">
        {currentOrPast > 0 && (
          <Box
            display="inline-block"
            width="22px"
            height="22px"
            style={{
              backgroundColor: "#cc0033",
              color: "white",
              borderRadius: "2px",
              textAlign: "center",
              fontSize: "12px",
              letterSpacing: "0.4px",
              paddingTop: "1px",
              marginRight: "8px",
            }}
          >
            {currentOrPast}
          </Box>
        )}
        {showFuture > 0 && (
          <Box
            display="inline-block"
            width="22px"
            height="22px"
            style={{
              backgroundColor: "#c6cacc",
              color: "#212121",
              borderRadius: "2px",
              textAlign: "center",
              fontSize: "12px",
              letterSpacing: "0.4px",
              paddingTop: "1px",
              marginRight: "8px",
            }}
          >
            {showFuture}
          </Box>
        )}
      </TableCell>
    );
  }

  if (type === "myHRBadges") {
    let requestCount = row?.requestCount;
    return (
      <TableCell align="center">
        {requestCount > 0 && (
          <Box
            display="inline-block"
            width="22px"
            height="22px"
            style={{
              backgroundColor: "#cc0033",
              color: "white",
              borderRadius: "2px",
              textAlign: "center",
              fontSize: "12px",
              letterSpacing: "0.4px",
              paddingTop: "1px",
              marginRight: "8px",
            }}
          >
            {requestCount}
          </Box>
        )}
      </TableCell>
    );
  }

  if (type === "importError") {
    let emailAddress = `${row.errorPayload?.existedEmail} (${row.errorPayload?.existedEmployee})`;
    return (
      <TableCell align={align}>
        {row.errorPayload ? (
          <span>
            {t(row[id])} {emailAddress}
          </span>
        ) : (
          <span>{t(row[id])}</span>
        )}
      </TableCell>
    );
  }

  if (type === "importSSHRError") {
    let emailAddress = `${row.errorPayload?.existedEmail} (${row.errorPayload?.existedEmployee})`;
    return (
      <TableCell align={align}>
        {row.errorPayload ? (
          <span>
            {t(row[id])} {emailAddress}
          </span>
        ) : (
          <span>{t(row[id])}</span>
        )}
      </TableCell>
    );
  }

  if (type === "localizedName") {
    return (
      <TableCell align={align}>
        <Box>{t(`MY_LEAVE_${row.type}`)}</Box>
      </TableCell>
    );
  }

  if (type === "leaveTypeName") {
    return (
      <TableCell align={align}>
        <Box>{t(`MY_LEAVE_${row.leaveType}`)}</Box>
      </TableCell>
    );
  }

  if (id === "typeName") {
    return (
      <TableCell align={align}>
        <Box>{t(`MY_LEAVE_${row.typeName}`)}</Box>
      </TableCell>
    );
  }

  if (type === "currency") {
    return (
      <TableCell align={align}>
        <OcCurrencyComponent
          value={Number(row[id])}
        >
        </OcCurrencyComponent>
      </TableCell>
    );
  }

  if (type === "copyButton") {
    let editable = true;
    return (
      <TableCell align={align} style={{ padding: "0px 8px" }}>
        <Box>
          <Button
            variant="contained"
            color="primary"
            disableElevation
            disabled={!canCopy || !row?.canCopy}
            onClick={editable ? () => handleCafeteriaAction("copy") : () => { }}
          >
            {t("MY_CAFETERIA_COPY")}
          </Button>
        </Box>
      </TableCell>
    );
  }

  if (type === "editButton") {
    let editable = inlineEdit;
    if (row?.menuItem?.planable === "true" && !row?.plan) {
      editable = false;
    }

    return (
      <TableCell align={align} style={{ padding: "0px 8px" }}>
        <Box display="flex" flexDirection="row" justifyContent="flex-end">
          <Box
            id={`menu-edit-${row ? row.index : "zero"}`}
            display="flex"
            flexDirection="row"
            justifyContent="center"
            alignItems="center"
            style={{
              border: `1px solid ${editable ? "#919699" : "#C6CACC"}`,
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              cursor: editable ? "pointer" : "auto",
            }}
            {...(editable && { onClick: () => handleCafeteriaAction("edit") })}
          >
            <InlineIcon
              width="18px"
              height="18px"
              icon={pencilIcon}
              color={editable ? "#919699" : "#C6CACC"}
            />
          </Box>
        </Box>
      </TableCell>
    );
  }

  if (type === "mySheetEditButton") {
    return (
      <TableCell align={align} style={{ padding: "0px 8px" }}>
        <Box display="flex" flexDirection="row" justifyContent="flex-end">
          <Box
            id={`menu-edit-${row ? row.index : "zero"}`}
            display="flex"
            flexDirection="row"
            justifyContent="center"
            alignItems="center"
            style={{
              border: `1px solid #919699`,
              width: "36px",
              height: "36px",
              borderRadius: "50%",
              cursor: "pointer",
            }}
            onClick={() => onSelectRow(row)}
          >
            <InlineIcon
              width="24px"
              height="24px"
              icon={pencilIcon}
              color={"#919699"}
            />
          </Box>
        </Box>
      </TableCell>
    );
  }

  if (type === "i18nDate") {
    const date = new Date(row[id]).toLocaleDateString();
    return <TableCell align={align}>{date}</TableCell>;
  }

  if (type === "taxState") {
    return (
      <TableCell align={align}>
        <ApplicationState label={t(row[id])} />
      </TableCell>
    );
  }

  if (type === "myTimeActions") {
    const buttonStyle = {
      border: "1px solid #919699",
      borderRadius: "50%",
      cursor: "pointer",
      width: "36px",
      height: "36px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      color: "#919699",
    };

    const handleSelectRow = ({ action, row }: { action: string; row: any }) => {
      const newData = { ...row };
      newData.sender = "table";

      if (typeof newData.date === "string") {
        newData.date = new Date(newData.date);
      }
      if (typeof newData.startTime === "string") {
        newData.startTime = new Date(newData.startTime);
      }
      if (typeof newData.endTime === "string") {
        newData.endTime = new Date(newData.endTime);
      }

      if (row?.myTimeType === "WORKING_TIME" && action === "add") {
        newData.myTimeType = "OVERTIME";
        newData.start = "";
        newData.startTime = "";
        newData.end = "";
        newData.endTime = "";
        newData.timeDataTypeCode = "";
        newData.timeDataTypeName = "";
      }
      onSelectRow({ action, row: newData });
    };

    const date = new Date().toLocaleDateString();
    const currentMonth = date.slice(6, 8);
    const periodMonth = new Date(row?.date).toLocaleDateString().slice(6, 8);

    return (
      <TableCell align={align}>
        <Box
          id={`add-${new Date(row?.date).getDate()}`}
          display="flex"
          flexDirection="row"
          justifyContent="center"
          style={{ minHeight: "38px" }}
        >
          <Box display="flex" flexDirection="row">
            <OcDataTableBodyCellAddIcon
              row={row}
              handleSelectRow={handleSelectRow}
              buttonStyle={buttonStyle as React.CSSProperties}
              isNotExaminer={isNotExaminer}
            />
            <OcDataTableBodyCellArrowIcon
              row={row}
              isNotExaminer={isNotExaminer}
              currentMonth={currentMonth}
              periodMonth={periodMonth}
              handleSelectRow={handleSelectRow}
              buttonStyle={buttonStyle as React.CSSProperties}
            />
            <OcDataTableBodyCellArrowIconIsNotExaminer
              row={row}
              currentMonth={currentMonth}
              isNotExaminer={isNotExaminer}
              buttonStyle={buttonStyle as React.CSSProperties}
              periodMonth={periodMonth}
            />
            <OcDataTableBodyCellEditIcon
              row={row}
              isNotExaminer={isNotExaminer}
              handleSelectRow={handleSelectRow}
              buttonStyle={buttonStyle as React.CSSProperties}
            />
          </Box>
        </Box>
      </TableCell>
    );
  }

  if (type === "myTimeType") {
    return (
      <TableCell align={align} style={{ color: color }}>
        {t(row[id])}
      </TableCell>
    );
  }

  return (
    <TableCell align={align} style={{ color: color }}>
      {row[id]}
    </TableCell>
  );
};

export default OcDataTableBodyCell;
